import {
  DataTable,
  EmptyState,
  formatCentsBRL,
  formatCount,
  formatDateOnly,
  formatNumber,
  MetricCard,
  PageLayout,
  SearchInput,
  SectionCard,
  SegmentedControl,
} from '@repo/ui'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  AlertTriangleIcon,
  BanknoteIcon,
  ClockIcon,
  ReceiptTextIcon,
  XCircleIcon,
} from 'lucide-react'
import { useEffect, useState } from 'react'

import { ErrorState } from '@/components/error-state'
import { InvoicesTable } from '@/components/invoices-table'
import { OrgCell } from '@/components/org-cell'
import { StatusBadge } from '@/components/status-badge'
import { useAdminOverview } from '@/hooks/use-admin-overview'
import { useInvoices } from '@/hooks/use-revenue'
import type { BillingInvoiceStatus } from '@/lib/api-types'

const STATUS_OPTIONS = [
  { value: 'all', label: 'Todas' },
  { value: 'PAID', label: 'Pagas' },
  { value: 'OPEN', label: 'Em aberto' },
  { value: 'UNCOLLECTIBLE', label: 'Incobráveis' },
  { value: 'VOID', label: 'Anuladas' },
] as const

const PAGE_SIZE = 25

type PaymentsSearch = {
  status?: BillingInvoiceStatus
  q?: string
  pagina?: number
}

export const Route = createFileRoute('/_app/pagamentos')({
  validateSearch: (search: Record<string, unknown>): PaymentsSearch => ({
    status: STATUS_OPTIONS.some(
      (option) => option.value !== 'all' && option.value === search.status
    )
      ? (search.status as BillingInvoiceStatus)
      : undefined,
    q: typeof search.q === 'string' && search.q ? search.q : undefined,
    pagina:
      typeof search.pagina === 'number' && search.pagina > 1
        ? Math.floor(search.pagina)
        : undefined,
  }),
  component: PaymentsPage,
})

function PaymentsPage() {
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  // A busca vai para a URL (e para a API) só depois de uma pausa na digitação.
  const [term, setTerm] = useState(search.q ?? '')
  useEffect(() => {
    const timer = setTimeout(() => {
      if ((search.q ?? '') !== term) {
        void navigate({
          search: (previous) => ({
            ...previous,
            q: term || undefined,
            pagina: undefined,
          }),
          replace: true,
        })
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [term, search.q, navigate])

  const invoices = useInvoices({
    status: search.status,
    search: search.q,
    page: search.pagina ?? 1,
    pageSize: PAGE_SIZE,
  })

  const layout = {
    icon: BanknoteIcon,
    title: 'Pagamentos',
    description: 'Faturas da Stripe, contratos e add-ons.',
  }

  if (invoices.isError) {
    return (
      <PageLayout {...layout}>
        <ErrorState onRetry={() => void invoices.refetch()} />
      </PageLayout>
    )
  }

  const data = invoices.data
  const summary = data?.summary
  const loading = invoices.isLoading
  const nothingYet = data && data.total === 0 && !search.status && !search.q

  return (
    <PageLayout {...layout} contentClassName="space-y-6">
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          label="Recebido no mês"
          value={formatCentsBRL(summary?.paidThisMonth.cents)}
          hint={formatCount(
            summary?.paidThisMonth.count,
            'fatura paga',
            'faturas pagas'
          )}
          icon={BanknoteIcon}
          loading={loading}
        />
        <MetricCard
          label="Em aberto"
          value={formatCentsBRL(summary?.open.cents)}
          hint={`${formatCount(summary?.open.count, 'fatura', 'faturas')} aguardando pagamento`}
          icon={ClockIcon}
          tone={summary && summary.open.count > 0 ? 'warning' : 'default'}
          loading={loading}
        />
        <MetricCard
          label="Falhas de cobrança no mês"
          value={formatNumber(summary?.failedThisMonth)}
          hint="A Stripe tenta cobrar de novo"
          icon={AlertTriangleIcon}
          tone={
            summary && summary.failedThisMonth > 0 ? 'destructive' : 'default'
          }
          loading={loading}
        />
        <MetricCard
          label="Incobráveis"
          value={formatCentsBRL(summary?.uncollectible.cents)}
          hint={`${formatCount(summary?.uncollectible.count, 'fatura dada', 'faturas dadas')} como perdida${summary?.uncollectible.count === 1 ? '' : 's'}`}
          icon={XCircleIcon}
          loading={loading}
        />
      </div>

      <SectionCard
        title="Faturas"
        description="Espelhadas da Stripe pelo webhook. Valores com cupom e impostos."
        contentClassName="p-0"
        actions={
          <>
            <SegmentedControl
              label="Status da fatura"
              options={STATUS_OPTIONS}
              value={search.status ?? 'all'}
              onChange={(value) =>
                void navigate({
                  search: (previous) => ({
                    ...previous,
                    status: value === 'all' ? undefined : value,
                    pagina: undefined,
                  }),
                  replace: true,
                })
              }
            />
            <SearchInput
              className="w-64"
              value={term}
              onChange={(event) => setTerm(event.target.value)}
              placeholder="Número, ID ou organização"
            />
          </>
        }
      >
        {nothingYet ? (
          <EmptyState
            variant="inline"
            icon={ReceiptTextIcon}
            title="Nenhuma fatura registrada ainda"
            description="As novas chegam pelo webhook da Stripe. Para trazer o histórico, rode no servidor: pnpm --filter @repo/server billing:backfill-invoices"
          />
        ) : (
          <InvoicesTable
            rows={data?.invoices ?? []}
            total={data?.total ?? 0}
            loading={loading}
            pageSize={PAGE_SIZE}
            page={search.pagina ?? 1}
            onPageChange={(pagina) =>
              void navigate({
                search: (previous) => ({
                  ...previous,
                  pagina: pagina > 1 ? pagina : undefined,
                }),
                replace: true,
              })
            }
          />
        )}
      </SectionCard>

      <ContractsSection />
    </PageLayout>
  )
}

/** Contratos Enterprise/Parceiro e add-ons ativos, registrados no banco. */
function ContractsSection() {
  const overview = useAdminOverview()
  const navigate = useNavigate()
  const rows =
    overview.data?.organizations.filter(
      (row) => row.contract || row.addons.quantity > 0
    ) ?? []

  return (
    <SectionCard
      title="Contratos e add-ons"
      description="Contratos Enterprise/Parceiro e add-ons ativos."
      contentClassName="p-0"
    >
      <DataTable
        variant="flush"
        rows={rows}
        loading={overview.isLoading}
        getRowKey={(row) => row.id}
        empty="Nenhum contrato ou add-on ativo."
        defaultSort={{ key: 'contract', direction: 'desc' }}
        onRowClick={(row) =>
          void navigate({
            to: '/organizacoes/$orgId',
            params: { orgId: row.id },
          })
        }
        columns={[
          {
            key: 'org',
            header: 'Organização',
            sortValue: (row) => row.name,
            render: (row) => (
              <OrgCell
                id={row.id}
                name={row.name}
                slug={row.slug}
                logo={row.logo}
              />
            ),
          },
          {
            key: 'plan',
            header: 'Plano',
            render: (row) =>
              row.contract ? (
                <StatusBadge value={row.contract.planType} tone="neutral" />
              ) : (
                <span className="text-muted-foreground">—</span>
              ),
          },
          {
            key: 'contract',
            header: 'Contrato/mês',
            align: 'right',
            sortValue: (row) => row.contract?.priceMonthlyBRL,
            render: (row) =>
              row.contract ? formatCentsBRL(row.contract.priceMonthlyBRL) : '—',
          },
          {
            key: 'addons',
            header: 'Add-ons/mês',
            align: 'right',
            sortValue: (row) => row.addons.monthlyValueBRL,
            render: (row) =>
              row.addons.quantity > 0
                ? `${formatCentsBRL(row.addons.monthlyValueBRL)} (${formatNumber(row.addons.quantity)})`
                : '—',
          },
          {
            key: 'starts',
            header: 'Início',
            sortValue: (row) => row.contract?.startsAt,
            render: (row) => formatDateOnly(row.contract?.startsAt),
          },
          {
            key: 'ends',
            header: 'Fim',
            render: (row) =>
              row.contract?.endsAt
                ? formatDateOnly(row.contract.endsAt)
                : row.contract
                  ? 'Sem prazo'
                  : '—',
          },
        ]}
      />
    </SectionCard>
  )
}
